// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { useMemo } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  ListGroup,
  ListGroupItem
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import SaasCrmDashboard from "../../saasCrm";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  LAYOUT_MIN_WIDTH_260,
  SUITE_PROGRESS_HEIGHT_8,
  SUITE_CRM_CONTACT_AVATAR,
  SAAS_CRM_HEALTH_BAR_WIDTH_GOOD,
  SAAS_CRM_HEALTH_BAR_WIDTH_WARN
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import {
  AsyncStateBoundary,
  SuitePageShell,
  KpiStatCards
} from "../../_suite/primitives";

const STAGES = ["Proposal", "Negotiation", "Closed won", "Discovery"];

function DealPipelinePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.crm.listDeals(),
    []
  );
  const columns = useMemo(() => {
    const deals = data || [];
    const m = {};
    STAGES.forEach(s => {
      m[s] = deals.filter(d => d.stage === s);
    });
    const rest = deals.filter(d => !STAGES.includes(d.stage));
    m.Other = rest;
    return m;
  }, [data]);

  const pipelineValue = useMemo(() => {
    return (data || []).reduce((s, d) => s + (d.value || 0), 0);
  }, [data]);

  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.crm.deals" />}
      afterBreadcrumb={
        <p className="text-muted small mb-0">
          Kanban by stage — drag-and-drop would use dnd-kit or similar.
        </p>
      }
    >
      <KpiStatCards
        items={[
          {
            title: "Open deals",
            value: (data || []).length,
            sub: "Pipeline"
          },
          {
            title: "Weighted pipeline",
            value: `$${Math.round(
              (data || []).reduce(
                (s, d) => s + (d.value || 0) * ((d.probability || 0) / 100),
                0
              ) / 1000
            )}k`,
            sub: "Probability × value"
          },
          {
            title: "Raw value",
            value: `$${Math.round(pipelineValue / 1000)}k`,
            sub: "Unweighted"
          }
        ]}
      />
      <AsyncStateBoundary loading={loading} error={error}>
        <Row className="flex-nowrap overflow-auto pb-3">
          {Object.entries(columns).map(([stage, deals]) => (
            <Col key={stage} style={LAYOUT_MIN_WIDTH_260} className="mb-3">
              <div className="bg-light rounded p-2 mb-2 font-weight-bold small text-uppercase text-muted d-flex justify-content-between">
                <span>{stage}</span>
                <span>{deals.length}</span>
              </div>
              {deals.map(d => (
                <Card key={d.id} className="mb-2 border-0 shadow-sm">
                  <CardBody className="py-3">
                    <div className="d-flex justify-content-between">
                      <strong>{d.name}</strong>
                      <Badge color="info">${(d.value / 1000).toFixed(0)}k</Badge>
                    </div>
                    <div className="small text-muted">{d.owner}</div>
                    {d.probability != null && (
                      <div className="small mt-1">
                        Win prob{" "}
                        <Badge color="light" className="text-dark">
                          {d.probability}%
                        </Badge>
                      </div>
                    )}
                    {d.stageEntered && (
                      <div className="small text-muted">
                        In stage since {d.stageEntered}
                      </div>
                    )}
                    {d.lastTouch && (
                      <div className="small font-italic mt-1">
                        Last touch: {d.lastTouch}
                      </div>
                    )}
                    <Button
                      tag={Link}
                      color="link"
                      size="sm"
                      className="px-0"
                      to={d.id}
                      relative="path"
                    >
                      Open deal room →
                    </Button>
                  </CardBody>
                </Card>
              ))}
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function DealDetailTimelinePage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.crm.getDeal(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.crm.deal-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← <IntlMessages id="suite.action.back" />
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Row>
            <Col md="7">
              <Card className="mb-4">
                <CardBody>
                  <h3>{data.name}</h3>
                  <Badge className="me-2">{data.stage}</Badge>
                  <p className="h4 text-success mb-0">${data.value}</p>
                  <p className="text-muted">Owner: {data.owner}</p>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <CardTitle tag="h5">Activity timeline</CardTitle>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-3 pb-3 border-start border-primary ps-3 ms-2">
                      <div className="small text-muted">Latest</div>
                      <div>{data.lastTouch || `Stage: ${data.stage}`}</div>
                    </li>
                    <li className="mb-3 pb-3 border-start border-light ps-3 ms-2">
                      <div className="small text-muted">Stage entry</div>
                      <div>
                        {data.stageEntered
                          ? `Entered ${data.stage} on ${data.stageEntered}`
                          : "Wire CRM activity API"}
                      </div>
                    </li>
                    <li className="mb-0 pb-0 border-start border-light ps-3 ms-2">
                      <div className="small text-muted">Forecast</div>
                      <div>
                        {data.probability != null
                          ? `${data.probability}% · $${data.value}`
                          : "—"}
                      </div>
                    </li>
                  </ul>
                </CardBody>
              </Card>
            </Col>
            <Col md="5">
              <Card className="bg-light border-0">
                <CardBody>
                  <CardTitle tag="h6">Next best actions</CardTitle>
                  <ListGroup flush>
                    <ListGroupItem className="bg-transparent border-0 px-0">
                      Send MSA for review
                    </ListGroupItem>
                    <ListGroupItem className="bg-transparent border-0 px-0">
                      Schedule security questionnaire
                    </ListGroupItem>
                  </ListGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function AccountsHealthPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.crm.listAccounts(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.crm.accounts" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(a => (
            <Col md="6" key={a.id} className="mb-4">
              <Card className="border-0 shadow-sm h-100">
                <CardBody>
                  <div className="d-flex justify-content-between mb-2">
                    <h5 className="mb-0">{a.name}</h5>
                    <Badge
                      color={a.health === "good" ? "success" : "warning"}
                    >
                      {a.health}
                    </Badge>
                  </div>
                  <p className="text-muted small mb-2">
                    MRR ${a.mrr}
                    {a.seats != null && (
                      <span className="ms-2">· {a.seats} seats</span>
                    )}
                  </p>
                  {a.expansionSignal && (
                    <p className="small border-start border-info ps-2 mb-2">
                      <strong>Signal:</strong> {a.expansionSignal}
                    </p>
                  )}
                  <div
                    className="progress mb-3"
                    style={SUITE_PROGRESS_HEIGHT_8}
                  >
                    <div
                      className={`progress-bar bg-${a.health === "good" ? "success" : "warning"}`}
                      style={
                        a.health === "good"
                          ? SAAS_CRM_HEALTH_BAR_WIDTH_GOOD
                          : SAAS_CRM_HEALTH_BAR_WIDTH_WARN
                      }
                    />
                  </div>
                  <Button
                    tag={Link}
                    color="outline-primary"
                    size="sm"
                    to={a.id}
                    relative="path"
                  >
                    Account 360
                  </Button>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function AccountDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.crm.getAccount(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.crm.account-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Row>
            <Col md="8">
              <Card className="mb-3">
                <CardBody>
                  <h3>{data.name}</h3>
                  <p>
                    Health: <Badge>{data.health}</Badge> · MRR ${data.mrr}
                    {data.seats != null && <> · {data.seats} seats</>}
                  </p>
                  {data.expansionSignal && (
                    <Card className="bg-light border-0">
                      <CardBody className="py-2">
                        <CardTitle tag="h6" className="mb-1">
                          Expansion signal
                        </CardTitle>
                        <p className="small mb-0">{data.expansionSignal}</p>
                      </CardBody>
                    </Card>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md="4">
              <Card>
                <CardBody>
                  <CardTitle tag="h6">Touch timeline</CardTitle>
                  <p className="small text-muted mb-0">
                    Wire Salesforce / HubSpot activity stream for calls, emails,
                    and meetings tied to this account.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function ContactsRolodexPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.crm.listContacts(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.crm.contacts" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <ListGroup className="shadow-sm">
          {(data || []).map(c => (
            <ListGroupItem
              key={c.id}
              className="d-flex align-items-center py-3"
            >
              <div
                className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                style={SUITE_CRM_CONTACT_AVATAR}
              >
                {String(c.name)
                  .split(" ")
                  .map(s => s[0])
                  .join("")
                  .slice(0, 2)}
              </div>
              <div className="flex-grow-1">
                <div className="font-weight-bold">{c.name}</div>
                <div className="small text-muted">{c.title}</div>
              </div>
              <a href={`mailto:${c.email}`} className="small">
                {c.email}
              </a>
            </ListGroupItem>
          ))}
        </ListGroup>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function ActivitiesFeedPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.crm.listActivities(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.crm.activities" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <div className="position-relative ps-3">
          {(data || []).map(a => (
            <div key={a.id} className="mb-4 ps-3 border-start border-2">
              <Badge color="light" className="text-dark mb-1">
                {a.type}
              </Badge>
              <div className="font-weight-medium">{a.subject}</div>
              <div className="small text-muted">
                Due {a.due} · {a.owner}
                {a.account && (
                  <span className="ms-1">
                    · <strong>{a.account}</strong>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

export function SaasCrmSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="saas-crm" />}>
        <Route index element={<SaasCrmDashboard />} />
        <Route path="deals" element={<DealPipelinePage />} />
        <Route path="deals/:id" element={<DealDetailTimelinePage />} />
        <Route path="accounts" element={<AccountsHealthPage />} />
        <Route path="accounts/:id" element={<AccountDetailPage />} />
        <Route path="contacts" element={<ContactsRolodexPage />} />
        <Route path="activities" element={<ActivitiesFeedPage />} />
      </Route>
    </Routes>
  );
}
